import styles from "./PriceCard.module.css";
import PropTypes from "prop-types";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ClearIcon from "@mui/icons-material/Clear";
import ButtonMd from "../../Button/ButtonMd";
import { buildSignupUrlWithMarketingParams } from "../../../utils/signupMarketingUrl";
import { useEffect, useMemo, useState } from "react";

const formatNaira = (value) => `₦${Number(value || 0).toLocaleString("en-US")}`;

/**
 * Resolve a price from billing_frequency map for the selected cadence.
 * Falls back to monthly, then legacy `price`, then 0.
 */
export const resolveFrequencyPrice = (billingFrequency, duration, legacyPrice = 0) => {
  if (billingFrequency && typeof billingFrequency === "object") {
    const mapped = billingFrequency[duration] ?? billingFrequency.monthly;
    if (mapped != null && mapped !== "") {
      return Number(mapped);
    }
  }

  return Number(legacyPrice || 0);
};

const PriceCard = ({
  amount,
  payType,
  planType,
  planDescription,
  modelId,
  price,
  billingFrequency,
  features,
  variants = [],
  duration = "monthly",
  maxUsers
}) => {
  const [selectedVariant, setSelectedVariant] = useState(null);

  useEffect(() => {
    if (planType === "Pro" && variants && variants.length > 0) {
      const proStarterVariant = variants.find((v) => v.name === "Pro Starters") ||
        variants.find((v) => v.name === "Pro 25");
      if (proStarterVariant) {
        setSelectedVariant(proStarterVariant);
      } else {
        setSelectedVariant(variants[0]);
      }
    } else {
      setSelectedVariant(null);
    }
  }, [variants, planType]);

  const activeBillingFrequency = selectedVariant?.billing_frequency || billingFrequency;
  const displayPrice = resolveFrequencyPrice(activeBillingFrequency, duration, selectedVariant?.price ?? price);
  const seatCount = selectedVariant?.max_users != null
    ? Number(selectedVariant.max_users)
    : Number(maxUsers || 0);

  const displayAmount = useMemo(() => {
    if (amount && !selectedVariant && !billingFrequency) {
      return amount;
    }

    return formatNaira(displayPrice);
  }, [amount, selectedVariant, billingFrequency, displayPrice]);

  const perStudentPrice = seatCount > 0 && displayPrice > 0
    ? Math.ceil(displayPrice / seatCount)
    : null;

  const periodLabel = duration === "quarterly" ? "quarter" : duration === "yearly" ? "year" : "month";

  const handleGetStarted = () => {
    if (displayPrice === 0) {
      window.open(buildSignupUrlWithMarketingParams(), "_blank", "noopener,noreferrer");

      return;
    }

    if (!modelId || !planType) {
      // eslint-disable-next-line no-console
      console.error("Missing pricing data.");

      return;
    }

    // changed the naming for security purposes
    // pmid = price_model_id
    // dur = duration / billing_frequency
    // pname = plan_name
    const pricingExtras =
      planType === "Pro" && selectedVariant
        ? {
          pmid: modelId,
          price: displayPrice,
          pname: planType,
          var_id: selectedVariant.id,
          variant_name: selectedVariant.name,
          dur: duration
        }
        : {
          pmid: modelId,
          price: displayPrice,
          pname: planType,
          dur: duration
        };

    const url = buildSignupUrlWithMarketingParams(pricingExtras);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className={`${styles.pricemain}`}>
      <div className={`${styles.pricecont}`}>
        <div className={styles.priceheading}>
          <h5>{planType}</h5>
          <h6>Starting From:</h6>
          <h3>
            {displayAmount}/{payType}
          </h3>
          {perStudentPrice != null ? (
            <span className={styles.perStudentBadge}>
              {formatNaira(perStudentPrice)} / student / {periodLabel}
            </span>
          ) : Number(displayPrice) === 0 && planType === "Free" ? (
            <span className={styles.perStudentBadge}>Free for every student</span>
          ) : null}
          <div className={styles.pricedesc}>{planDescription}</div>
        </div>
        <div className={`${styles.subdesc}`}>

          {features?.map((feat, i) => {
            return (
              <div key={i} className={`${styles.featuredesc} flex gap-8 align-item-center`}>
                {feat === "NULL" ? <ClearIcon className="icon-red" /> : <CheckCircleIcon className="light-blue" />}
                <p>{feat === "NULL" ? "--------------------------------------------" : feat}</p>
              </div>
            );
          })}
        </div>
        <ButtonMd title="Get Started" onClick={handleGetStarted} />
      </div>
    </div>
  );
};
PriceCard.propTypes = {
  amount: PropTypes.string,
  payType: PropTypes.string,
  planType: PropTypes.string,
  planDescription: PropTypes.string,
  price: PropTypes.number,
  billingFrequency: PropTypes.object,
  modelId: PropTypes.string,
  duration: PropTypes.string,
  maxUsers: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  features: PropTypes.array,
  variants: PropTypes.array
};

export default PriceCard;
